import { useEffect, useState } from "react";
import { Bookmark, ChefHat, NotebookPen, ArrowLeft, ArrowRight, X } from "lucide-react";

// Key bumped to v2 alongside the copy refresh (Saved shelf naming + Back
// button) so returning visitors see the updated tour exactly once.
const DONE_KEY = "willoah.grovetour.done.v2";

// Copy note: we deliberately avoid "branch" / "seed" here so first-time
// visitors don't have to learn the metaphor before they even use the app.
// "Idea" is the neutral shared vocabulary; users learn the seed→branch
// framing once they plant their first one.
const STEPS = [
  {
    icon: Bookmark,
    eyebrow: "step one",
    title: "Save an idea",
    body: "Tap the bookmark on any idea to tuck it into your Saved shelf — private and only visible to you on your own Roots page.",
    cta: "Next",
  },
  {
    icon: ChefHat,
    eyebrow: "step two",
    title: "Cook hands-free",
    body: "Every recipe has Cook Mode — a full-screen step viewer that reads each step aloud and runs timers for you while your hands are floury.",
    cta: "Next",
  },
  {
    icon: NotebookPen,
    eyebrow: "step three",
    title: "Write a Field Note",
    body: "After you've actually tried something you saved, come back and share what worked, what didn't, and who it's not for. Honest reviews only — never star ratings.",
    cta: "Got it",
  },
];

export default function GroveTour() {
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return !localStorage.getItem(DONE_KEY);
    } catch {
      return true;
    }
  });
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") finish();
      if (e.key === "ArrowRight" || e.key === "Enter") next();
      if (e.key === "ArrowLeft") back();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  const finish = () => {
    try { localStorage.setItem(DONE_KEY, "1"); } catch {}
    setOpen(false);
  };

  const next = () => {
    if (step >= STEPS.length - 1) finish();
    else setStep((s) => s + 1);
  };
  const back = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  if (!open) return null;
  const S = STEPS[step];
  const Icon = S.icon;

  return (
    <div
      data-testid="grove-tour"
      role="dialog"
      aria-modal="true"
      aria-labelledby="grove-tour-title"
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-[#2A2823]/50 backdrop-blur-sm"
    >
      <div
        className="relative w-full sm:max-w-md m-4 sm:m-0 rounded-3xl bg-[#FBF8F1] border border-[#B79256]/40 shadow-[0_16px_48px_-16px_rgba(42,40,35,0.35)] overflow-hidden"
        style={{ animation: "grovetourIn 400ms ease-out both" }}
      >
        <button
          type="button"
          data-testid="grove-tour-close"
          onClick={finish}
          aria-label="Close tour"
          className="absolute top-3 right-3 p-2 rounded-full text-[#2A2823]/50 hover:text-[#2A2823] hover:bg-[#B79256]/15 transition-colors"
        >
          <X className="w-4 h-4" strokeWidth={1.75} />
        </button>

        <div className="px-8 pt-9 pb-6">
          <div className="flex items-center gap-2">
            <span
              data-testid="grove-tour-eyebrow"
              className="accent-label text-[#B79256]"
            >
              {S.eyebrow}
            </span>
            <span className="text-xs text-[#2A2823]/40">·</span>
            <span
              data-testid="grove-tour-progress"
              className="text-xs text-[#2A2823]/50"
            >
              {step + 1} of {STEPS.length}
            </span>
          </div>

          <div className="mt-5 flex items-start gap-4">
            <span className="shrink-0 w-11 h-11 rounded-full bg-[#4E5A3D]/10 text-[#4E5A3D] flex items-center justify-center">
              <Icon className="w-5 h-5" strokeWidth={1.75} />
            </span>
            <div>
              <h2
                id="grove-tour-title"
                data-testid="grove-tour-title"
                className="font-serif-display text-3xl text-[#2A2823] leading-tight"
              >
                {S.title}
              </h2>
              <p
                data-testid="grove-tour-body"
                className="mt-3 text-[#2A2823]/75 leading-relaxed"
              >
                {S.body}
              </p>
            </div>
          </div>
        </div>

        <div className="px-8 pb-7 pt-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="grove-tour-skip"
              onClick={finish}
              className="text-sm text-[#2A2823]/50 hover:text-[#2A2823] transition-colors"
            >
              Skip tour
            </button>
            {step > 0 && (
              <button
                type="button"
                data-testid="grove-tour-back"
                onClick={back}
                aria-label="Previous step"
                className="inline-flex items-center gap-1 text-sm text-[#4E5A3D] hover:text-[#3E4830] transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
                Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-1.5" aria-hidden="true">
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === step
                      ? "w-6 bg-[#4E5A3D]"
                      : i < step
                        ? "w-1.5 bg-[#4E5A3D]/60"
                        : "w-1.5 bg-[#B79256]/40"
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              data-testid="grove-tour-next"
              onClick={next}
              className="inline-flex items-center gap-2 rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] px-5 h-10 text-sm shadow-none transition-colors"
            >
              {S.cta}
              {step < STEPS.length - 1 && (
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes grovetourIn {
          from { opacity: 0; transform: translateY(12px) scale(0.985); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="grove-tour"] > div { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
